const IS_DEV = process.env.NODE_ENV === 'development';
const API_BASE = process.env.REACT_APP_API_BASE_URL || (IS_DEV ? 'http://localhost:8080' : '');

export function apiUrl(path) {
  if (path.startsWith('http')) return path;
  if (!API_BASE) {
    throw new Error('REACT_APP_API_BASE_URL must be set for non-development builds.');
  }
  return `${API_BASE}${path}`;
}

function readSession() {
  try {
    return JSON.parse(localStorage.getItem('session') || 'null');
  } catch {
    return null;
  }
}

function onAuthPage() {
  const p = window.location.pathname || '/';
  return p === '/' || p === '/login' || p.startsWith('/signup');
}

function clearAllSession() {
  try { localStorage.removeItem('session'); } catch {}
  try { localStorage.removeItem('accessToken'); } catch {}
  try { localStorage.removeItem('selectedCustomerId'); } catch {}
  try { localStorage.removeItem('customerId'); } catch {}
  try { localStorage.removeItem('customerName'); } catch {}
  try { localStorage.removeItem('userId'); } catch {}
  try { localStorage.removeItem('userid'); } catch {}
  try { sessionStorage.clear(); } catch {}
}

export function clearClientSession() {
  clearAllSession();
}

function clearSessionAndRedirect() {
  clearAllSession();
  if (!onAuthPage()) window.location.replace('/');
}

function notify(msg) {
  try { alert(msg); } catch {}
}

function effectiveCustomerId(sess) {
  const picked = localStorage.getItem('selectedCustomerId');
  if (picked != null && picked !== '') return Number(picked);
  return sess?.customerId ?? null;
}

function isFormData(v) {
  return typeof FormData !== 'undefined' && v instanceof FormData;
}

function isBlobLike(v) {
  return typeof Blob !== 'undefined' && v instanceof Blob;
}

function extractErrorMessage(status, data) {
  if (data && typeof data === 'object') {
    if (typeof data.message === 'string' && data.message) return data.message;
    if (typeof data.error === 'string' && data.error) return data.error;
    if (typeof data.detail === 'string' && data.detail) return data.detail;
  }
  if (typeof data === 'string' && data) return data;
  return `HTTP ${status}`;
}

async function request(path, { method = 'GET', headers = {}, body, skipCustomer = false, responseType = 'auto' } = {}) {
  const url = apiUrl(path);
  const sess = readSession();
  const custId = effectiveCustomerId(sess);
  const token = localStorage.getItem('accessToken');

  const sendingForm = isFormData(body);
  const sendingBlob = isBlobLike(body);

  const finalHeaders = {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(!skipCustomer && custId ? { 'X-Customer-Id': String(custId) } : {}),
    ...headers,
  };

  if (body !== undefined && !sendingForm && !sendingBlob && typeof body !== 'string') {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (body !== undefined && typeof body === 'string') {
    finalHeaders['Content-Type'] = finalHeaders['Content-Type'] ?? 'application/json';
  }

  const init = { method, headers: finalHeaders };

  if (body !== undefined) {
    if (sendingForm || sendingBlob) {
      init.body = body;
    } else if (typeof body === 'string') {
      init.body = body;
    } else {
      init.body = JSON.stringify(body);
    }
  }

  const res = await fetch(url, init);

  const ct = res.headers.get('content-type') || '';
  let data;
  if (responseType === 'blob') {
    data = await res.blob().catch(() => null);
  } else if (responseType === 'text') {
    data = await res.text().catch(() => '');
  } else if (responseType === 'json') {
    data = await res.json().catch(() => null);
  } else {
    data = ct.includes('application/json')
      ? await res.json().catch(() => null)
      : await res.text().catch(() => '');
  }

  if (res.status === 401) {
    clearSessionAndRedirect();
    if (!onAuthPage()) notify('Session expired. Please sign in.');
    const err = new Error(extractErrorMessage(401, data));
    err.status = 401;
    err.data = data;
    throw err;
  }

  if (res.status === 403) {
    if (!onAuthPage()) notify("You don’t have access to this.");
    const err = new Error(extractErrorMessage(403, data));
    err.status = 403;
    err.data = data;
    throw err;
  }

  if (!res.ok) {
    const msg = extractErrorMessage(res.status, data);
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return { data, status: res.status };
}

export const api = {
  get: (p, opts) => request(p, { ...opts, method: 'GET' }),
  post: (p, body, opts) => request(p, { ...opts, method: 'POST', body }),
  put: (p, body, opts) => request(p, { ...opts, method: 'PUT', body }),
  del: (p, opts) => request(p, { ...opts, method: 'DELETE' }),
  getBlob: (p, opts) => request(p, { ...opts, method: 'GET', responseType: 'blob' }),
};
